import { useEffect } from 'react'
import { useAtom } from 'jotai'
import { chatContextLockedAtom } from '../../../../context/TableToChatContext'

export interface TableSelectionData {
  rowCount: number
  columnCount: number
  rowLabels: string[]
  columnLabels: string[]
  cellData: Array<{
    row: number
    col: number
    value: string
    rowLabel: string
    columnLabel: string
    rowData?: any // Add the actual row data object
  }>
}

interface UseAutoContextSelectionProps {
  tableSelection: TableSelectionData | null
  customers: any[]
  products: any[]
  onContextItemsChange: (items: Set<string>) => void
  selectedContextItems: Set<string>
  revenueWaterfallData?: any
}

// Simple and direct entity resolution using the actual row data
const resolveEntitiesFromTableSelection = (
  tableSelection: TableSelectionData,
  customers: any[],
  products: any[]
) => {
  const resolvedCustomers = new Set<string>()
  const resolvedProducts = new Set<string>()
  const resolvedSchedules = new Set<string>()

  // Get unique row data from all cells (avoid duplicates)
  const uniqueRowData = new Map<number, any>()

  tableSelection.cellData.forEach(cell => {
    if (cell.rowData && !uniqueRowData.has(cell.row)) {
      uniqueRowData.set(cell.row, cell.rowData)
    }
  })

  // Process each unique row
  uniqueRowData.forEach(rowData => {
    // Direct customer ID extraction from rowData (trust rowData regardless of customers list load)
    if (rowData.customerId) {
      resolvedCustomers.add(rowData.customerId)
    }

    // Product resolution based on row type and data (best-effort)
    if (rowData.type === 'product' && (rowData.productId || rowData.id)) {
      const pid = rowData.productId || rowData.id
      if (pid) {
        const product = products.find(p => p.id === pid)
        if (product) {
          resolvedProducts.add(product.id)
        }
      }
    }
  })

  // Fallback: If no direct IDs found, try label matching as a last resort
  if (resolvedCustomers.size === 0 && uniqueRowData.size > 0) {
    // Try using the row labels for exact matching
    tableSelection.rowLabels.forEach(rowLabel => {
      if (!rowLabel || rowLabel.length < 3) return

      const exactCustomer = customers.find(
        customer =>
          customer.legalName === rowLabel ||
          customer.label === rowLabel ||
          customer.name === rowLabel ||
          customer.displayName === rowLabel
      )

      if (exactCustomer) {
        resolvedCustomers.add(exactCustomer.id)
      }
    })
  }

  return { resolvedCustomers, resolvedProducts, resolvedSchedules }
}

export const useAutoContextSelection = ({
  tableSelection,
  customers,
  products,
  onContextItemsChange,
  selectedContextItems,
  revenueWaterfallData
}: UseAutoContextSelectionProps) => {
  const [locked] = useAtom(chatContextLockedAtom)

  useEffect(() => {
    // When locked (after Ask AI), do not auto-add any further context items
    if (locked) return
    if (!tableSelection) return

    // Continue even if customers array not yet loaded; we can still add IDs

    const newContextItems = new Set(selectedContextItems)
    let hasChanges = false

    // Use the enhanced lookup function to resolve entities
    const { resolvedCustomers, resolvedProducts, resolvedSchedules } =
      resolveEntitiesFromTableSelection(tableSelection, customers, products)

    // Add resolved customers
    resolvedCustomers.forEach(customerId => {
      const contextId = `customer:${customerId}`
      if (!newContextItems.has(contextId)) {
        newContextItems.add(contextId)
        hasChanges = true
      }
    })

    // Add resolved products
    resolvedProducts.forEach(productId => {
      const contextId = `product:${productId}`
      if (!newContextItems.has(contextId)) {
        newContextItems.add(contextId)
        hasChanges = true
      }
    })

    // Add resolved schedules
    resolvedSchedules.forEach(scheduleId => {
      const contextId = `schedule:${scheduleId}`
      if (!newContextItems.has(contextId)) {
        newContextItems.add(contextId)
        hasChanges = true
      }
    })

    // If we have revenue data in the selection, add revenue context
    const hasRevenueData = tableSelection.columnLabels.some(
      col =>
        col.toLowerCase().includes('total') ||
        col.toLowerCase().includes('recognized') ||
        col.toLowerCase().includes('remaining') ||
        col.toLowerCase().includes('booked')
    )

    if (hasRevenueData) {
      const revenueContextId = 'revenue:waterfall'
      if (!newContextItems.has(revenueContextId)) {
        newContextItems.add(revenueContextId)
        hasChanges = true
      }
    }

    // If multiple customers are selected, also add "All Customers" context
    if (resolvedCustomers.size > 2) {
      const allCustomersId = 'customer:all'
      if (!newContextItems.has(allCustomersId)) {
        newContextItems.add(allCustomersId)
        hasChanges = true
      }
    }

    // If multiple products are selected, also add "All Products" context
    if (resolvedProducts.size > 2) {
      const allProductsId = 'product:all'
      if (!newContextItems.has(allProductsId)) {
        newContextItems.add(allProductsId)
        hasChanges = true
      }
    }

    // Only update if we found new context items
    if (hasChanges) {
      onContextItemsChange(newContextItems)
    }
  }, [
    tableSelection,
    customers,
    products,
    onContextItemsChange,
    selectedContextItems,
    revenueWaterfallData,
    locked
  ])
}
